<template>
  <div class="drivers-occupancy-list">
    <div ref="occupancyToolbar" class="d-flex align-center px-4 pt-2" style="gap: 16px;">
      <v-text-field
        v-model="searchQuery"
        placeholder="Szukaj kierowcy..."
        prepend-inner-icon="mdi-magnify"
        hide-details
        dense
        outlined
        clearable
        style="max-width: 320px;"
      />
      <v-spacer />
      <TablePersonalization
        table-name="driversOccupancy"
        :config="{ parameters }"
        action="driversOccupancy/setTablePersonalization"
        button-text="Dostosuj kolumny"
        class="mb-1"
      />
      <v-btn :disabled="isRefreshing" color="primary" outlined class="px-4 mb-1" @click="refreshDriversOccupancy">
        <v-icon v-if="isRefreshing" color="primary" size="20" class="mr-2 mdi-spin">
          mdi-loading
        </v-icon>
        <v-icon v-else color="primary" size="20" class="mr-2">
          mdi-reload
        </v-icon>
        Odśwież
      </v-btn>
    </div>
    <v-data-table
      ref="driversTable"
      class="drivers-occupancy-table"
      :items="parsedDriversList"
      :headers="headers"
      item-key="rowKey"
      :items-per-page="-1"
      :expanded.sync="expandedItems"
      hide-default-footer
      fixed-header
      :height="tableHeight"
      :loading="isProcessing"
    >
      <template #item="{ item, expand, isExpanded }">
        <DriversOccupancyRow
          :item="item"
          :headers="headers"
          :row-actions="rowActions"
          :actions-offset="actionsOffset"
          :is-expanded="isExpanded"
          @expand="toggleRowExpand(item, expand, isExpanded)"
        />
      </template>
      <template #expanded-item="{ headers: expandedHeaders, item: expandedItem }">
        <tr>
          <td :colspan="expandedHeaders.length" style="padding: 0;">
            <v-data-table
              class="drivers-occupancy-table drivers-occupancy-table--expanded"
              :headers="headers"
              :items="expandedItem.groupedCourses"
              item-key="rowKey"
              :items-per-page="-1"
              hide-default-footer
              hide-default-header
            >
              <template #header>
                <tr>
                  <th
                    v-for="(parameter, headerIndex) in headers"
                    :key="headerIndex"
                    :style="{ width: `${parameter.width}px`, height: '0px' }"
                  />
                </tr>
              </template>
              <template #item="{ item: childItem }">
                <DriversOccupancyRow
                  :item="childItem"
                  :headers="headers"
                  :row-actions="rowActions"
                  :actions-offset="actionsOffset"
                  is-child-row
                />
              </template>
            </v-data-table>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import debounce from 'lodash/debounce'
import TablePersonalization from '../Table/TablePersonalization'
import DriversOccupancyRow from './DriversOccupancyRow'
import tableActionsOffsetMixin from '../../mixins/tableActionsOffsetMixin'
import driversOccupancyTableHeightMixin from '../../mixins/driversOccupancyTableHeightMixin'
import { DRIVERS_OCCUPANCY_VIEW } from '../../store/driversOccupancy'
import { getDriverDistanceToDestination, parseDriverOccupancyRow } from '../../utils/driversOccupancyRows'

const EXPAND_COLUMN = {
  name: 'expand',
  text: '',
  value: 'expand',
  show: true,
  sortable: false,
  width: 48
}

export default {
  components: {
    TablePersonalization,
    DriversOccupancyRow
  },
  mixins: [tableActionsOffsetMixin, driversOccupancyTableHeightMixin],
  data: () => ({
    searchInput: '',
    isRefreshing: false,
    tableActionsRefName: 'driversTable',
    rowActions: [],
    expandedItems: [],
    expandedDriverIds: []
  }),
  computed: {
    ...mapState({
      isOccupancyDialogOpen: state => state.driversOccupancy.isOccupancyDialogOpen,
      driversList: state => state.driversOccupancy.items,
      parameters: state => state.driversOccupancy.parameters,
      isProcessing: state => state.driversOccupancy.isProcessing,
      query: state => state.driversOccupancy.query,
      viewStep: state => state.driversOccupancy.viewStep
    }),
    searchQuery: {
      get() {
        return this.searchInput
      },
      set(value) {
        this.searchInput = value
        this.debouncedSearch(value || '')
      }
    },
    headers() {
      const visibleParameters = this.parameters.filter(parameter => parameter.show)
      const expandColumn = visibleParameters.find(parameter => parameter.name === 'expand') || EXPAND_COLUMN
      const otherParameters = visibleParameters.filter(parameter => parameter.name !== 'expand')

      return [expandColumn, ...otherParameters]
    },
    tableActionsOffsetActive() {
      return this.isOccupancyDialogOpen && this.viewStep === DRIVERS_OCCUPANCY_VIEW.LIST
    },
    tableActionsOffsetLayoutVersion() {
      return `${this.headers.length}-${this.parsedDriversList.length}`
    },
    parsedDriversList() {
      return [...this.driversList]
        .sort((a, b) => {
          return (
            b.available - a.available ||
            a.remainingCoursesCount - b.remainingCoursesCount ||
            getDriverDistanceToDestination(
              a.distanceToClient,
              a.distanceToDepartment,
              a.timeToClient,
              a.timeToDepartment
            ) -
              getDriverDistanceToDestination(
                b.distanceToClient,
                b.distanceToDepartment,
                b.timeToClient,
                b.timeToDepartment
              )
          )
        })
        .map(parseDriverOccupancyRow)
    }
  },
  watch: {
    parsedDriversList(newList) {
      if (!this.expandedDriverIds.length) return

      this.$nextTick(() => {
        this.expandedItems = newList.filter(
          item => item.isExpandable && this.expandedDriverIds.includes(item.id)
        )
      })
    }
  },
  created() {
    this.searchInput = this.query
    this.debouncedSearch = debounce(value => {
      this.setDriversQuery(value)
    }, 400)
    this.rowActions = [
      {
        name: 'showSuggestedCourses',
        text: 'Pokaż sugerowane kursy',
        icon: 'mdi-truck-delivery',
        target: 'function',
        callback: item => this.loadSuggestedLeaveCourses(item)
      }
    ]
  },
  methods: {
    ...mapActions({
      getDriversOccupancy: 'driversOccupancy/getDriversOccupancy',
      setDriversQuery: 'driversOccupancy/setDriversQuery',
      loadSuggestedLeaveCourses: 'driversOccupancy/loadSuggestedLeaveCourses'
    }),
    toggleRowExpand(item, expand, isExpanded) {
      expand(!isExpanded)

      if (isExpanded) {
        this.expandedDriverIds = this.expandedDriverIds.filter(id => id !== item.id)
        return
      }

      if (!this.expandedDriverIds.includes(item.id)) {
        this.expandedDriverIds.push(item.id)
      }
    },
    refreshDriversOccupancy() {
      this.isRefreshing = true
      this.getDriversOccupancy({ includeTimes: true }).finally(() => {
        this.isRefreshing = false
      })
    }
  }
}
</script>

<style lang="scss">
.drivers-occupancy-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.drivers-occupancy-table {
  flex: 1;
  min-height: 0;

  table {
    width: 100%;
  }

  .v-data-table__wrapper {
    overflow-x: auto;
  }
  .v-data-table-header th,
  .v-data-table-header th > span {
    white-space: nowrap !important;
  }
}

.drivers-occupancy-table--expanded {
  .v-data-table__wrapper {
    overflow: visible;
  }
}
</style>
